import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Avatar from '../../../base/avatar/components/Avatar';
import { getParticipantDisplayName } from '../../../base/participants/functions';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { stopReceiver } from '../../actions';
/**
 * How many presses of Escape end the session, and how long the run may take.
 * It is deliberately a gesture rather than a single key: Escape closes dialogs
 * and menus all over this application, and none of those should hang up on
 * somebody mid-sentence.
 */
const PANIC_PRESSES = 3;
const PANIC_WINDOW = 1500;
const useStyles = makeStyles()(theme => {
    return {
        edge: {
            position: 'fixed',
            inset: 0,
            zIndex: 250,
            pointerEvents: 'none',
            boxShadow: `inset 0 0 0 3px ${theme.palette.warning01},
                        inset 0 0 90px -30px ${theme.palette.warning01}`
        },
        banner: {
            position: 'fixed',
            top: theme.spacing(3),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 251,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(3),
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxWidth: 'calc(100vw - 32px)',
            padding: `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(3)}`,
            borderRadius: '999px',
            background: `${theme.palette.ui01}F2`,
            backdropFilter: 'blur(16px)',
            border: `1px solid ${theme.palette.warning01}`,
            boxShadow: '0 12px 40px -8px rgba(0, 0, 0, .6)'
        },
        identity: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            minWidth: 0
        },
        text: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0
        },
        title: {
            ...theme.typography.bodyShortBold,
            color: theme.palette.text01,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        hint: {
            ...theme.typography.labelRegular,
            color: theme.palette.text02,
            whiteSpace: 'nowrap'
        }
    };
});
/**
 * Shown to the person whose computer is being controlled, for as long as it is.
 *
 * There is no version of this that can be dismissed or missed: somebody else's
 * hands are on this machine, and stopping them has to be one unmistakable click
 * away at all times.
 *
 * @returns {ReactElement|null}
 */
const ReceiverBanner = () => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const escapes = useRef([]);
    const controller = useSelector((state) => state['features/remote-control'].receiver.controller);
    const controllerName = useSelector((state) => (controller ? getParticipantDisplayName(state, controller) : ''));
    const onStop = useCallback(() => {
        dispatch(stopReceiver());
    }, [dispatch]);
    useEffect(() => {
        if (!controller) {
            return;
        }
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            const now = Date.now();
            escapes.current = [...escapes.current, now].filter(at => now - at < PANIC_WINDOW);
            if (escapes.current.length >= PANIC_PRESSES) {
                escapes.current = [];
                onStop();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [controller, onStop]);
    if (!controller) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { "aria-hidden": true, className: classes.edge }),
        React.createElement("div", { "aria-live": 'assertive', className: classes.banner, role: 'status' },
            React.createElement("div", { className: classes.identity },
                React.createElement(Avatar, { participantId: controller, size: 32 }),
                React.createElement("div", { className: classes.text },
                    React.createElement("span", { className: classes.title }, t('remoteControl.beingControlled', { user: controllerName })),
                    React.createElement("span", { className: classes.hint }, t('remoteControl.panicHint')))),
            React.createElement(Button, { accessibilityLabel: t('remoteControl.stopSharingControl'), labelKey: 'remoteControl.stopSharingControl', onClick: onStop, size: 'small', type: BUTTON_TYPES.DESTRUCTIVE }))));
};
export default ReceiverBanner;
